import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  FormComponentType, 
  IFormControlChange, 
  IFormValidationErrors
} from './df-form-controls/df-form-preview-interfaces'

// Import form control components
import { DfFormInput } from './df-form-controls/df-form-input'
import { DfFormTextarea } from './df-form-controls/df-form-textarea'
import { DfFormSelect } from './df-form-controls/df-form-select'
import { DfFormCheckbox } from './df-form-controls/df-form-checkbox'
import { DfFormRadio } from './df-form-controls/df-form-radio'
import { DfFormSegment } from './df-form-controls/df-form-segment'
import { DfFormDateTime } from './df-form-controls/df-form-date-time'
import { DfFormSignature } from './df-form-controls/df-form-signature'
import { DfFormHeading } from './df-form-controls/df-form-heading'
import { DfFormFileUpload } from './df-form-controls/df-form-file-upload'
import { DfFormLocation } from './df-form-controls/df-form-location'
import { DfFormComments } from './df-form-controls/df-form-comments'
import { DfFormInstruction } from './df-form-controls/df-form-instruction'
import { DfFormSection } from './df-form-controls/df-form-section/df-form-section'
import { DfFormDataGrid } from './df-form-controls/df-form-grid/df-form-grid'

// Import DfFormTable directly instead of lazy loading to ensure it's included in the build
import { DfFormTable } from './df-form-controls/df-form-table/df-form-table'

// Import styles - these will be processed by the build system
// DfFormPreview.scss already imports df-form-controls.scss, so we only need to import this
import './DfFormPreview.scss'

export type DeviceType = 'desktop' | 'tablet' | 'mobile'

export interface DfFormPreviewProps {
  formComponents: FormComponentType[]
  formData?: any
  currentDevice?: DeviceType
  isPreviewMode?: boolean
  initialFormData?: FormComponentType[]
  onSubmit?: (formData: FormComponentType[]) => void
  onFormDataChange?: (formData: FormComponentType[]) => void
}

export const DfFormPreview: React.FC<DfFormPreviewProps> = ({
  formComponents = [],
  currentDevice = 'desktop',
  isPreviewMode = false,
  initialFormData = [],
  onSubmit,
  onFormDataChange
}) => {
  const { t } = useTranslation()
  
  // Form state
  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [validationErrors, setValidationErrors] = useState<IFormValidationErrors>({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({})
  const [componentVisibility, setComponentVisibility] = useState<Record<string, boolean>>({})

  // Initialize form state
  useEffect(() => {
    initializeFormState()
  }, [formComponents, initialFormData])

  const initializeFormState = useCallback(() => {
    const initialValues: Record<string, any> = {}
    
    // Initialize form values from initial data
    if (initialFormData && initialFormData.length > 0) {
      initialFormData.forEach(component => {
        if ('defaultValue' in component.basic && component.basic.defaultValue !== undefined && component.basic.defaultValue !== null) {
          initialValues[component.id] = component.basic.defaultValue
        }
      })
    }
    
    // Initialize form values from form components (for submitted data)
    if (formComponents && formComponents.length > 0) {
      formComponents.forEach(component => {
        // Check if component has a value in basic.value (submitted data)
        if ('value' in component.basic && component.basic.value !== undefined && component.basic.value !== null && component.basic.value !== '') {
          initialValues[component.id] = component.basic.value
        }
        // Fallback to defaultValue if no value
        else if ('defaultValue' in component.basic && component.basic.defaultValue !== undefined && component.basic.defaultValue !== null) {
          initialValues[component.id] = component.basic.defaultValue
        }
      })
    }
    
    setFormValues(initialValues)
    
    // Evaluate initial conditional logic
    evaluateConditionalLogic()
  }, [initialFormData, formComponents])

  // Evaluate conditional logic for all components
  const evaluateConditionalLogic = useCallback(() => {
    const visibility: Record<string, boolean> = {}
    
    formComponents.forEach(component => {
      // Skip conditional logic for table and datagrid components - they are always visible
      if (component.name === 'table' || component.name === 'datagrid') {
        visibility[component.id] = true
      } else if (component.conditional && component.conditional.conditions && component.conditional.conditions.length > 0) {
        // Evaluate conditional logic with new structure
        const result = evaluateCondition(component.conditional, formComponents, formValues)
        visibility[component.id] = result
      } else {
        // No conditional logic means always show
        visibility[component.id] = true
      }
    })
    
    setComponentVisibility(visibility)
  }, [formComponents, formValues])

  // Condition evaluation using new IConditionalLogic structure
  const evaluateCondition = (conditional: any, _components: FormComponentType[], values: Record<string, any>) => {
    if (!conditional.conditions || conditional.conditions.length === 0) return true
    
    const { action, when, conditions } = conditional
    
    // Evaluate each condition
    const conditionResults = conditions.map((condition: any) => {
      const actualValue = values[condition.when]
      const expectedValue = condition.value
      
      switch (condition.operator) {
        case 'equals':
          return actualValue === expectedValue
        case 'notEquals':
          return actualValue !== expectedValue
        case 'isEmpty':
          return actualValue === null || actualValue === undefined || actualValue === '' || (Array.isArray(actualValue) && actualValue.length === 0)
        case 'isNotEmpty':
          return actualValue !== null && actualValue !== undefined && actualValue !== '' && !(Array.isArray(actualValue) && actualValue.length === 0)
        case 'contains':
          return String(actualValue).includes(String(expectedValue))
        case 'notContains':
          return !String(actualValue).includes(String(expectedValue))
        case 'greaterThan':
          return Number(actualValue) > Number(expectedValue)
        case 'lessThan':
          return Number(actualValue) < Number(expectedValue)
        case 'greaterThanOrEqual':
          return Number(actualValue) >= Number(expectedValue)
        case 'lessThanOrEqual':
          return Number(actualValue) <= Number(expectedValue)
        default:
          return true
      }
    })
    
    // Combine results based on 'when' (all or any)
    const combinedResult = when === 'all' 
      ? conditionResults.every((result: boolean) => result === true)
      : conditionResults.some((result: boolean) => result === true)
    
    // Apply action (show, hide, always)
    if (action === 'always') {
      return true
    } else if (action === 'hide') {
      return !combinedResult
    } else {
      // action === 'show'
      return combinedResult
    }
  }

  // Check if a component should be visible based on conditional logic
  const shouldShowComponent = useCallback((componentId: string): boolean => {
    // Find the component to check its type
    const component = formComponents.find(comp => comp.id === componentId)
    
    // Table and datagrid components are always visible (no conditional logic)
    if (component && (component.name === 'table' || component.name === 'datagrid')) {
      return true
    }
    
    return componentVisibility[componentId] !== false
  }, [componentVisibility, formComponents])

  // Handle form value changes and re-evaluate conditional logic
  const onFormValueChange = useCallback((change: IFormControlChange) => {
    setFormValues(prev => ({
      ...prev,
      [change.id]: change.value
    }))
    
    // Update form components
    const updatedComponents = formComponents.map(component => {
      if (component.id === change.id) {
        if ('defaultValue' in component.basic) {
          return {
            ...component,
            basic: {
              ...component.basic,
              value: change.value ?? component.basic.defaultValue,
              defaultValue: change.value
            }
          } as FormComponentType
        }
      }
      return component
    })
    
    onFormDataChange?.(updatedComponents)
    
    // Clear validation errors when user starts typing
    if (validationErrors[change.id]) {
      setValidationErrors(prev => ({
        ...prev,
        [change.id]: ''
      }))
    }
  }, [formComponents, validationErrors, onFormDataChange])

  // Handle form control blur
  const onFormControlBlur = useCallback((fieldId: string) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldId]: true
    }))
    validateField(fieldId)
  }, [])

  // Handle form control focus
  const onFormControlFocus = useCallback((fieldId: string) => {
    // Clear validation errors when field is focused
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }))
    }
  }, [validationErrors])

  // Validate individual field
  const validateField = useCallback((fieldId: string) => {
    // Only validate in test mode
    if (isPreviewMode) return
    
    const component = formComponents.find(comp => comp.id === fieldId)
    if (!component) return

    // Get current value from form values (real-time input)
    const currentValue = formValues[fieldId] || ''
    
    // Check if field has a value
    const hasValue = currentValue !== null && currentValue !== undefined && 
      !(typeof currentValue === 'string' && currentValue.trim() === '') &&
      !(Array.isArray(currentValue) && currentValue.length === 0)

    let errorMessage = ''

    // Check for required validation
    if (component.validation.required && !hasValue) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} is required`
    }
    // Check for email validation
    else if (component.name === 'email-input' && hasValue && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(currentValue)) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be a valid email`
    }
    // Check for minLength validation
    else if (component.validation.minLength && hasValue && currentValue.length < component.validation.minLength) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be at least ${component.validation.minLength} characters`
    }
    // Check for maxLength validation
    else if (component.validation.maxLength && hasValue && currentValue.length > component.validation.maxLength) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be no more than ${component.validation.maxLength} characters`
    }
    // Check for number validations
    else if (component.name === 'number-input' && hasValue) {
      const numValue = parseFloat(currentValue)
      if (isNaN(numValue)) {
        const customMessage = component.validation.customValidationMessage
        const fieldLabel = component.basic.label || 'This field'
        errorMessage = customMessage || `${fieldLabel} must be a valid number`
      } else {
        if (component.validation.min !== undefined && numValue < component.validation.min) {
          const customMessage = component.validation.customValidationMessage
          const fieldLabel = component.basic.label || 'This field'
          errorMessage = customMessage || `${fieldLabel} must be at least ${component.validation.min}`
        } else if (component.validation.max !== undefined && numValue > component.validation.max) {
          const customMessage = component.validation.customValidationMessage
          const fieldLabel = component.basic.label || 'This field'
          errorMessage = customMessage || `${fieldLabel} must be no more than ${component.validation.max}`
        }
      }
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [fieldId]: errorMessage
    }))
  }, [formComponents, isPreviewMode, formValues])

  // Disabled automatic validation to let DfFormInput handle its own validation
  // This prevents conflicts between external and local validation
  // useEffect(() => {
  //   if (!isPreviewMode && Object.keys(formValues).length > 0) {
  //     // Validate all form components when form values change (but not on initial load)
  //     formComponents.forEach(component => {
  //       validateField(component.id)
  //     })
  //   }
  // }, [formValues, validateField, isPreviewMode, formComponents])

  // Enhanced form validity check
  const isFormValid = useCallback((): boolean => {
    return formComponents.every(component => {
      // Use real-time form values instead of component values
      const currentValue = formValues[component.id] || ''
      
      // Check if field has a value
      let hasValue = false
      
      if (Array.isArray(currentValue)) {
        // For array values (checkbox, multi-select), check if array has items
        hasValue = currentValue.length > 0
      } else if (typeof currentValue === 'string') {
        // For string values, check if not empty after trimming
        hasValue = currentValue.trim() !== ''
      } else {
        // For other types, check if not null/undefined
        hasValue = currentValue !== null && currentValue !== undefined
      }
      
      if (component.validation.required && !hasValue) {
        return false
      }
      return true
    })
  }, [formComponents, formValues])

  // Validate all fields
  const validateAllFields = useCallback((): boolean => {
    // Only validate in test mode
    if (isPreviewMode) return true
    
    let isValid = true
    setFormSubmitted(true)
    const errors: IFormValidationErrors = {}

    formComponents.forEach(component => {
      // Use real-time form values instead of component values
      const currentValue = formValues[component.id] || ''
      
      // Check if field has a value
      let hasValue = false
      
      if (Array.isArray(currentValue)) {
        // For array values (checkbox, multi-select), check if array has items
        hasValue = currentValue.length > 0
      } else if (typeof currentValue === 'string') {
        // For string values, check if not empty after trimming
        hasValue = currentValue.trim() !== ''
      } else {
        // For other types, check if not null/undefined
        hasValue = currentValue !== null && currentValue !== undefined
      }

      if (component.validation.required && !hasValue) {
        const customMessage = component.validation.customValidationMessage
        const fieldLabel = component.basic.label || 'This field'
        const errorMessage = customMessage || `${fieldLabel} is required`
        errors[component.id] = errorMessage
        isValid = false
      } else {
        errors[component.id] = ''
      }

      setTouchedFields(prev => ({
        ...prev,
        [component.id]: true
      }))
    })

    setValidationErrors(errors)
    return isValid
  }, [formComponents, isPreviewMode, formValues])

  // Handle form submission
  const handleSubmit = useCallback(() => {
    setFormSubmitted(true)

    if (!validateAllFields()) {
      // Focus on the first invalid field
      const firstErrorField = formComponents.find(comp => {
        let value = ''
        if ('value' in comp.basic && comp.basic.value !== undefined) {
          value = comp.basic.value
        } else if ('defaultValue' in comp.basic && comp.basic.defaultValue !== undefined) {
          value = comp.basic.defaultValue
        }
        return comp.validation.required && !value
      })
      if (firstErrorField) {
        const errorElement = document.getElementById(firstErrorField.id)
        if (errorElement) {
          errorElement.focus()
        }
      }
      return
    }

    // Prepare final form data
    const finalFormData = formComponents.map(component => {
      const userValue = formValues[component.id]
      let finalValue = userValue
      
      if (finalValue === undefined) {
        if ('value' in component.basic && component.basic.value !== undefined) {
          finalValue = component.basic.value
        } else if ('defaultValue' in component.basic && component.basic.defaultValue !== undefined) {
          finalValue = component.basic.defaultValue
        } else {
          finalValue = ''
        }
      }

      if ('defaultValue' in component.basic) {
        return {
          ...component,
          basic: {
            ...component.basic,
            value: finalValue,
            defaultValue: finalValue
          }
        } as FormComponentType
      }
      
      return component
    })

    onSubmit?.(finalFormData)
  }, [formComponents, formValues, validateAllFields, onSubmit])

  // Get preview classes
  const getPreviewClasses = useCallback(() => {
    return {
      [`device-${currentDevice}`]: true,
      readonly: isPreviewMode
    }
  }, [currentDevice, isPreviewMode])

  // Render form component based on type
  const renderFormComponent = (component: FormComponentType) => {
    const formValue = formValues[component.id]
    
    const commonProps = {
      id: component.id,
      validationErrors,
      formValue: formValue,
      touchedFields,
      formSubmitted,
      mode: (isPreviewMode ? 'preview' : 'test') as 'preview' | 'test',
      readonly: isPreviewMode,
      onValueChange: onFormValueChange,
      onBlur: () => onFormControlBlur(component.id),
      onFocus: () => onFormControlFocus(component.id)
    }

    switch (component.name) {
      case 'text-input':
      case 'number-input':
      case 'email-input':
        return (
          <DfFormInput
            {...commonProps}
            properties={component as any}
            inputType={component.name === 'text-input' ? 'text' : 
                      component.name === 'number-input' ? 'number' : 'email'}
          />
        )
      
      case 'textarea':
        return <DfFormTextarea {...commonProps} properties={component as any} />
      
      case 'select':
        // Map basic.options to top-level options for select component
        const selectProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormSelect {...commonProps} properties={selectProps as any} {...((commonProps as any).showCommentsInPreview !== undefined ? { showCommentsInPreview: true } : {})} />
      
      case 'checkbox':
        // Map basic.options to top-level options for checkbox component
        const checkboxProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormCheckbox {...commonProps} properties={checkboxProps as any} {...((commonProps as any).showCommentsInPreview !== undefined ? { showCommentsInPreview: true } : {})} />
      
      case 'radio':
        // Map basic.options to top-level options for radio component
        const radioProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormRadio {...commonProps} properties={radioProps as any} {...((commonProps as any).showCommentsInPreview !== undefined ? { showCommentsInPreview: true } : {})} />
      
      case 'segment':
        // Map basic.options to top-level options for segment component
        const segmentProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormSegment {...commonProps} properties={segmentProps as any} showCommentsInPreview={true} />
      
      case 'date':
      case 'date-picker':
        return <DfFormDateTime {...commonProps} properties={component as any} />
      
      case 'datetime-picker':
        return <DfFormDateTime {...commonProps} properties={component as any} />
      
      case 'signature':
        return <DfFormSignature {...commonProps} properties={component as any} />
      
      case 'heading':
        return (
          <DfFormHeading
            {...commonProps}
            properties={component as any}
          />
        )
      
      case 'instructions':
        return (
          <DfFormInstruction
            {...commonProps}
            properties={component as any}
          />
        )
      
      case 'location':
        return <DfFormLocation {...commonProps} properties={component as any} />
      
      case 'file-upload':
      case 'file':
        return <DfFormFileUpload {...commonProps} properties={component as any} />
      
      case 'section':
        return (
          <DfFormSection
            {...commonProps}
            properties={component as any}
            formData={formValues}
            renderFormComponent={(field: FormComponentType) => {
              return renderFormComponent(field)
            }}
          />
        )
      
      case 'table':
        return (
          <DfFormTable 
            {...commonProps} 
            properties={component as any}
            formData={formValues}
            renderFormComponent={(field: FormComponentType) => {
              return renderFormComponent(field)
            }}
          />
        )
      
      case 'datagrid':
        return (
          <DfFormDataGrid
            {...commonProps}
            properties={component as any}
            formData={formValues}
            renderFormComponent={(field: FormComponentType) => {
              return renderFormComponent(field)
            }}
          />
        )
      
      default:
        return (
          <div className="form-group">
            <div className="form-group-label">
              Unsupported Component: {component.name}
            </div>
          </div>
        )
    }
  }

  return (
    <div className="form-preview-container">
      <div className="form-preview-wrapper">
        <div className={`form-preview ${Object.entries(getPreviewClasses()).map(([key, value]) => value ? key : '').join(' ')}`}>
          {/* Form Canvas Header - You can implement this separately */}
          <div className="form-canvas-header">
            {/* <FormCanvasHeader 
              formData={formData}
              disableSaveDraft={false}
              disableCancel={false}
              disableSubmit={false}
              showTags={false}
            /> */}
          </div>

          {formComponents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-card">
                <div className="empty-state-preview-area">
                  <span className="empty-state-placeholder">
                    Add components to see the form preview
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <form className="form-preview-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              {formComponents.map((component) => (
                <div 
                  key={component.id} 
                  className="form-group"
                  style={{ display: shouldShowComponent(component.id) ? 'block' : 'none' }}
                >
                  {renderFormComponent(component)}
                </div>
              ))}
              
              {!isPreviewMode && (
                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={!isFormValid()}
                    className="form-submit-button"
                  >
                    {t('formBuilder.submit', 'Submit')}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default DfFormPreview
